
import React, { useState, useMemo, memo, useEffect } from 'react';
import './LazyImage.css';
import { secureImageUrl } from './utils/secureImageUrl';
import { resolveFirebaseImageUrl } from './utils/resolveFirebaseImageUrl';

const LazyImage = ({ 
  src, 
  alt, 
  style, 
  placeholder = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudGFsIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5IiBmb250LXNpemU9IjE0cHgiPkxvYWRpbmc8L3RleHQ+PC9zdmc+",
  className,
  onClick,
  onError,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [resolvedSrc, setResolvedSrc] = useState(null);

  // Resolve Firebase Storage URLs to signed URLs for iOS compatibility
  useEffect(() => {
    let isMounted = true;
    
    async function resolveSrc() {
      if (!src) {
        setResolvedSrc(null);
        return;
      }
      
      try {
        // First apply HTTPS conversion
        const secureSrc = secureImageUrl(src);
        
        // Then resolve Firebase Storage URLs for native platforms
        const finalSrc = await resolveFirebaseImageUrl(secureSrc);
        
        if (isMounted) {
          setResolvedSrc(finalSrc);
        }
      } catch (error) {
        console.error('Failed to resolve image URL:', error);
        if (isMounted) {
          setResolvedSrc(secureImageUrl(src));
        }
      }
    }
    
    resolveSrc();
    
    return () => {
      isMounted = false;
    };
  }, [src]);

  // Use resolved src for rendering
  const secureSrc = resolvedSrc || secureImageUrl(src);

  const handleLoad = () => {
    console.log("✅ Immagine caricata correttamente:", secureSrc);
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = (e) => {
    console.error("❌ Errore caricamento immagine:", secureSrc, e);
    setHasError(true);
    setIsLoaded(false);
    
    // Retry logic solo per domini fidati (imgur, base64)
    if (retryCount < 2 && secureSrc && typeof secureSrc === 'string' && 
        (secureSrc.includes('imgur.com') || secureSrc.startsWith('data:'))) {
      setTimeout(() => {
        console.log("🔄 Retry caricamento immagine:", retryCount + 1);
        setRetryCount(prev => prev + 1);
        setHasError(false);
        // Force reload con cache busting
        const img = new Image();
        img.onload = handleLoad;
        img.onerror = (retryError) => {
          console.warn("⚠️ Retry fallito:", retryError);
          setHasError(true);
        };
        img.src = secureSrc.startsWith('data:') ? secureSrc : secureSrc + '?t=' + Date.now();
      }, 1000 * (retryCount + 1));
    }
    
    if (onError) {
      onError(e);
    }
  };

  // Controllo validità URL
  const isValidUrl = secureSrc && 
    typeof secureSrc === 'string' && 
    secureSrc.trim() !== '' && 
    !secureSrc.includes('undefined') &&
    !secureSrc.includes('null') &&
    (secureSrc.startsWith('http') || secureSrc.startsWith('data:'));

  if (!isValidUrl) {
    console.log("⚠️ URL immagine non valido:", src);
    return (
      <div 
        className="lazy-image-no-image"
        style={{
          ...style,
          borderRadius: style?.borderRadius || 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          background: '#f5f5f5',
          color: '#999'
        }}
      >
        📦
      </div>
    );
  }

  const containerStyle = useMemo(() => ({
    position: 'relative',
    width: style?.width || 'auto',
    height: style?.height || 'auto',
    margin: style?.margin || 0
  }), [style]);

  const borderRadiusStyle = useMemo(() => ({
    borderRadius: style?.borderRadius || 0
  }), [style?.borderRadius]);

  return (
    <div className="lazy-image-container" style={containerStyle}>
      {/* Placeholder mentre l'immagine carica */}
      {!isLoaded && !hasError && (
        <div 
          className="lazy-image-placeholder"
          style={{
            ...style,
            ...borderRadiusStyle
          }}
        >
          Loading...
        </div>
      )}
      
      {/* Immagine principale */}
      <img
        src={secureSrc}
        alt={alt}
        style={style}
        className={`lazy-image ${className || ''} ${isLoaded ? 'loaded' : 'loading'}`}
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        {...props}
      />
      
      {/* Fallback per errori */}
      {hasError && retryCount >= 2 && (
        <div 
          className="lazy-image-error"
          style={{
            ...style,
            ...borderRadiusStyle,
            background: '#f5f5f5',
            color: '#999',
            fontSize: '32px'
          }}
        >
          📦
        </div>
      )}
    </div>
  );
};

export default memo(LazyImage);
